import React, { useState, useEffect } from 'react';
import { Building2, Check, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { useToastContext } from '../../contexts/ToastContext.jsx';
import { useNavigate } from 'react-router-dom';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import EmbeddedOnboardingFallback from './EmbeddedOnboardingFallback.jsx';
import './BankAccount.scss';
import './EmbeddedOnboardingFallback.scss';

const BankAccount = () => {
    const { technician, urlParams } = useAuth();
    const { showSuccess, showError, showInfo } = useToastContext();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [accountStatus, setAccountStatus] = useState(null);
    const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
    const [accountSession, setAccountSession] = useState(null);
    const [showEmbeddedOnboarding, setShowEmbeddedOnboarding] = useState(false);
    const [useEmbedded, setUseEmbedded] = useState(true); // Toggle for embedded vs redirect
    const [embeddedError, setEmbeddedError] = useState(null);
    const [initializationError, setInitializationError] = useState(null);

    useEffect(() => {
        fetchAccountStatus();
        initializeStripeConnect();
    }, []);

    const initializeStripeConnect = async () => {
        try {
            setInitializationError(null);

            // Fetch publishable key from server
            const configResponse = await fetch('/api/payments/config', {
                credentials: 'include'
            });
            const configData = await configResponse.json();
            const publishableKey = configData?.data?.publishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

            if (!publishableKey) {
                throw new Error('Stripe publishable key not configured');
            }

            // Initialize Stripe Connect
            const stripeInstance = await loadConnectAndInitialize({
                publishableKey: publishableKey,
                fetchClientSecret: async () => {
                    try {
                        // This will be called when we need a client secret
                        const response = await createAccountSession();
                        if (!response?.data?.clientSecret) {
                            throw new Error('Failed to obtain client secret');
                        }
                        return response.data.clientSecret;
                    } catch (error) {
                        console.error('Error fetching client secret:', error);
                        setEmbeddedError(error);
                        throw error;
                    }
                },
                appearance: {
                    overlays: 'dialog',
                    variables: {
                        colorPrimary: '#3b82f6',
                    }
                }
            });
            
            setStripeConnectInstance(stripeInstance);
            setInitializationError(null);
        } catch (error) {
            console.error('Error initializing Stripe Connect:', error);
            setInitializationError(error);
            setUseEmbedded(false); // Fall back to redirect flow
            
            // Only show error toast if it's not a configuration issue
            if (!error.message?.includes('publishable key')) {
                showError('Failed to initialize embedded setup. Using redirect method instead.');
            }
        }
    };

    const fetchAccountStatus = async () => {
        try {
            const response = await fetch(`/api/payments/technicians/${technician.id}/bank-account?location=${urlParams.location}&user=${urlParams.user}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const result = await response.json();
            if (result.success && result.data) {
                setAccountStatus(result.data);
            }
        } catch (error) {
            console.error('Error fetching bank account status:', error);
        }
    };

    const createAccountSession = async () => {
        try {
            const response = await fetch(`/api/payments/technicians/${technician.id}/embedded-onboarding/create-session?location=${urlParams.location}&user=${urlParams.user}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            if (result.success) {
                setAccountSession(result.data);
                return result;
            } else {
                throw new Error(result.message || 'Failed to create account session');
            }
        } catch (error) {
            console.error('Error creating account session:', error);
            throw error;
        }
    };

    const handleStartEmbeddedOnboarding = async () => {
        setLoading(true);
        setEmbeddedError(null);
        showInfo('Preparing embedded onboarding...');

        try {
            if (!stripeConnectInstance) {
                // Try to reinitialize if instance is null
                await initializeStripeConnect();
                
                if (!stripeConnectInstance) {
                    throw new Error('Stripe Connect could not be initialized');
                }
            }

            await createAccountSession();
            setShowEmbeddedOnboarding(true);
            showInfo('Loading onboarding form...');
        } catch (error) {
            console.error('Error starting embedded onboarding:', error);
            setEmbeddedError(error);
            showError('Failed to start embedded onboarding.');
        } finally {
            setLoading(false);
        }
    };

    const handleRetryEmbedded = async () => {
        setEmbeddedError(null);
        await initializeStripeConnect();
        if (stripeConnectInstance) {
            await handleStartEmbeddedOnboarding();
        }
    };

    const handleFallbackToRedirect = () => {
        setEmbeddedError(null);
        setUseEmbedded(false);
        setShowEmbeddedOnboarding(false);
    };

    const handleStartRedirectOnboarding = async () => {
        setLoading(true);
        showInfo('Creating Stripe Connect onboarding link...');

        try {
            const response = await fetch(`/api/payments/technicians/${technician.id}/bank-account?location=${urlParams.location}&user=${urlParams.user}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            
            if (result.success && result.data.onboardingUrl) {
                showInfo('Redirecting to Stripe Connect onboarding...');
                window.location.href = result.data.onboardingUrl;
            } else {
                showError('Failed to create onboarding flow: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error creating onboarding flow:', error);
            showError('Failed to create onboarding flow. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleOnboardingExit = async () => {
        setShowEmbeddedOnboarding(false);
        showInfo('Onboarding closed. Checking account status...');
        
        // Refresh account status after onboarding
        setTimeout(() => {
            fetchAccountStatus();
        }, 1000);
    };

    const handleOnboardingComplete = async () => {
        try {
            if (accountSession?.accountId) {
                const response = await fetch(`/api/payments/technicians/${technician.id}/embedded-onboarding/complete?location=${urlParams.location}&user=${urlParams.user}`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        accountId: accountSession.accountId
                    })
                });

                const result = await response.json();
                if (result.success) {
                    setAccountStatus(result.data);
                    showSuccess('Bank account setup completed successfully!');
                } else {
                    showError('Failed to complete onboarding: ' + (result.message || 'Unknown error'));
                }
            }
        } catch (error) {
            console.error('Error completing onboarding:', error);
            showError('Failed to complete onboarding. Please try again.');
        }
        
        setShowEmbeddedOnboarding(false);
        fetchAccountStatus();
    };

    const handleBack = () => {
        const params = new URLSearchParams();
        if (urlParams.location) params.set('location', urlParams.location);
        if (urlParams.user) params.set('user', urlParams.user);
        navigate('/dashboard?' + params.toString());
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'verified': return '#10b981';
            case 'pending': return '#f59e0b';
            case 'failed': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'verified': return 'Verified';
            case 'pending': return 'Pending Verification';
            case 'failed': return 'Verification Failed';
            case 'new': return 'Not Submitted';
            default: return 'Unknown';
        }
    };

    // Render embedded onboarding component
    if (showEmbeddedOnboarding) {
        // Show fallback if there's an error or missing dependencies
        if (embeddedError || !stripeConnectInstance || !accountSession) {
            return (
                <div className="bank-account">
                    <div className="bank-account__header">
                        <button className="back-button" onClick={handleBack}>
                            <ArrowLeft size={20} />
                            Back to Dashboard
                        </button>
                        
                        <div className="bank-account__title">
                            <Building2 size={32} />
                            <div>
                                <h1>Bank Account Setup</h1>
                                <p>Setting up embedded onboarding</p>
                            </div>
                        </div>
                    </div>

                    <div className="bank-account__content">
                        <EmbeddedOnboardingFallback
                            error={embeddedError || initializationError}
                            onRetry={handleRetryEmbedded}
                            onFallbackToRedirect={handleFallbackToRedirect}
                            loading={loading}
                        />
                    </div>
                </div>
            );
        }

        // Render successful embedded onboarding
        return (
            <div className="bank-account">
                <div className="bank-account__header">
                    <button className="back-button" onClick={handleBack}>
                        <ArrowLeft size={20} />
                        Back to Dashboard
                    </button>
                    
                    <div className="bank-account__title">
                        <Building2 size={32} />
                        <div>
                            <h1>Bank Account Setup</h1>
                            <p>Complete your bank account setup to receive payments</p>
                        </div>
                    </div>
                </div>

                <div className="bank-account__content">
                    <div className="embedded-onboarding-container">
                        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                            <ConnectAccountOnboarding
                                onExit={handleOnboardingExit}
                                onComplete={handleOnboardingComplete}
                                onStepChange={(stepChange) => {
                                    console.log('Onboarding step changed:', stepChange);
                                }}
                            />
                        </ConnectComponentsProvider>
                    </div>

                    <div className="onboarding-help">
                        <div className="security-notice">
                            <AlertCircle size={16} />
                            <div>
                                <strong>Secure Setup Process</strong>
                                <p>This form is powered by Stripe Connect's secure onboarding. Your information is encrypted and never stored on our servers.</p>
                            </div>
                        </div>
                        
                        <button 
                            type="button" 
                            className="btn-secondary"
                            onClick={handleOnboardingExit}
                        >
                            Cancel Setup
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bank-account">
            <div className="bank-account__header">
                <button className="back-button" onClick={handleBack}>
                    <ArrowLeft size={20} />
                    Back to Dashboard
                </button>
                
                <div className="bank-account__title">
                    <Building2 size={32} />
                    <div>
                        <h1>Bank Account Information</h1>
                        <p>Set up your bank account to receive reward payments from your company</p>
                    </div>
                </div>
            </div>

            <div className="bank-account__content">
                {accountStatus && (
                    <div className="account-status">
                        <div className="status-header">
                            <h3>Current Status</h3>
                            <span 
                                className="status-badge" 
                                style={{ backgroundColor: getStatusColor(accountStatus.status) }}
                            >
                                {getStatusText(accountStatus.status)}
                            </span>
                        </div>
                        
                        {accountStatus.last4 && (
                            <p>Bank Account: ••••{accountStatus.last4} ({accountStatus.bankName})</p>
                        )}
                        
                        {accountStatus.status === 'failed' && (
                            <div className="error-message">
                                <AlertCircle size={16} />
                                <span>Verification failed. Please check your account details and try again.</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="bank-account__form">
                    <h3>{accountStatus ? 'Update Bank Account' : 'Set Up Bank Account'}</h3>
                    
                    <div className="onboarding-info">
                        <p>To receive reward payments, you'll need to set up your bank account through Stripe's secure onboarding process.</p>
                        
                        <div className="onboarding-features">
                            <div className="feature">
                                <Check size={16} />
                                <span>Secure bank account verification</span>
                            </div>
                            <div className="feature">
                                <Check size={16} />
                                <span>Identity verification for compliance</span>
                            </div>
                            <div className="feature">
                                <Check size={16} />
                                <span>Tax information collection</span>
                            </div>
                            <div className="feature">
                                <Check size={16} />
                                <span>Fast and secure payouts</span>
                            </div>
                        </div>

                        <div className="security-notice">
                            <AlertCircle size={16} />
                            <div>
                                <strong>Powered by Stripe Connect</strong>
                                <p>Your information is processed securely through Stripe's industry-leading payment platform. We never store your bank account details.</p>
                            </div>
                        </div>

                        {/* Enhanced Setup Options */}
                        <div className="setup-method-selector">
                            <h4>Choose Setup Method:</h4>
                            
                            {initializationError && (
                                <div className="setup-warning">
                                    <AlertCircle size={16} />
                                    <div>
                                        <strong>Embedded Setup Unavailable</strong>
                                        <p>
                                            {initializationError.message?.includes('publishable key') 
                                                ? 'Stripe configuration missing. Using redirect method.' 
                                                : 'Unable to initialize embedded setup. Using redirect method.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            <div className="method-options">
                                <div className={`method-option ${initializationError ? 'disabled' : ''}`}>
                                    <input 
                                        type="radio" 
                                        id="embedded" 
                                        name="setupMethod" 
                                        checked={useEmbedded} 
                                        onChange={() => setUseEmbedded(true)}
                                        disabled={!!initializationError}
                                    />
                                    <label htmlFor="embedded">
                                        <strong>Embedded Setup (Recommended)</strong>
                                        <p>Complete setup in a secure way without leaving this page</p>
                                    </label>
                                </div>
                                <div className="method-option">
                                    <input 
                                        type="radio" 
                                        id="redirect" 
                                        name="setupMethod" 
                                        checked={!useEmbedded} 
                                        onChange={() => setUseEmbedded(false)}
                                    />
                                    <label htmlFor="redirect">
                                        <strong>Redirect Setup</strong>
                                        <p>Complete setup on Stripe's secure site</p>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="btn-secondary"
                                onClick={handleBack}
                                disabled={loading}
                            >
                                Back to Dashboard
                            </button>
                            <button 
                                type="button" 
                                className="btn-primary"
                                onClick={useEmbedded ? handleStartEmbeddedOnboarding : handleStartRedirectOnboarding}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {useEmbedded ? 'Preparing...' : 'Creating Link...'}
                                    </>
                                ) : (
                                    useEmbedded ? 'Start Embedded Setup' : 'Start Bank Account Setup'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankAccount;
